/**
 * Council Trail v0.1 — Hill Climber Lab REST client.
 *
 * Dev-surface endpoints that write synthetic propose_fork proposals into
 * the queue, bypassing the real peer-LLM path. Useful for testing the
 * chip → dialog → approve loop without a working peer turn pipeline.
 */
export interface InjectProposalInput {
  persona_id?: string;
  parent_message_id?: string;
  content?: string;
  rationale?: string;
}

export interface InjectProposalOutput {
  id: string;
  kind: string;
  status: 'pending' | 'approved' | 'dismissed';
  proposed_by_persona_id: string;
  target: Record<string, unknown>;
  rationale: string;
}

export interface InjectProposalsBulkOutput {
  injected: Array<{ id: string; kind: string }>;
  count: number;
}

export interface WipeProposalsOutput {
  wiped: number;
}

async function _json<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const body = await resp.text().catch(() => '');
    throw new Error(`council-lab api ${resp.status}: ${body || resp.statusText}`);
  }
  return resp.json() as Promise<T>;
}

export const councilLabApiClient = {
  async injectProposal(
    sessionId: string,
    payload: InjectProposalInput = {},
  ): Promise<InjectProposalOutput> {
    return _json<InjectProposalOutput>(
      await fetch(
        `/api/council-lab/sessions/${sessionId}/inject-proposal`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      ),
    );
  },

  async injectProposals(
    sessionId: string,
    count: number,
  ): Promise<InjectProposalsBulkOutput> {
    return _json<InjectProposalsBulkOutput>(
      await fetch(
        `/api/council-lab/sessions/${sessionId}/inject-proposals?count=${count}`,
        {
          method: 'POST',
          credentials: 'same-origin',
        },
      ),
    );
  },

  async wipeProposals(sessionId: string): Promise<WipeProposalsOutput> {
    return _json<WipeProposalsOutput>(
      await fetch(
        `/api/council-lab/sessions/${sessionId}/proposals`,
        {
          method: 'DELETE',
          credentials: 'same-origin',
        },
      ),
    );
  },

  // Arc 1a: fire a mock squad via the lab endpoint. Backend gates the
  // route behind ENABLE_SQUAD_MOCK_LAB; flag-off → 404. Server-side
  // mock_run=True is hardcoded by the route handler; no LLM, $0 cost.
  async fireSquadMock(
    sessionId: string,
    memberCount: number = 3,
  ): Promise<{
    squad_id: string;
    member_count: number;
    persona_ids: string[];
    pattern: string;
    mock_run: boolean;
    instructions: string;
  }> {
    return _json(
      await fetch(
        `/api/council-lab/sessions/${sessionId}/squad-mock`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ member_count: memberCount }),
        },
      ),
    );
  },
};
